<template>
  <div class="background-step">
    <h2 class="step-title">Professional Background</h2>
    <p class="step-description">Tell us about your professional experience</p>
    
    <div class="background-content">
      <div class="form-group">
        <label class="background-label">Rate your experience in the following areas</label>
        
        <div class="background-areas">
          <div class="background-area">
            <div class="area-header">
              <div class="area-icon">
                <v-icon size="large">mdi-office-building</v-icon>
              </div>
              <div class="area-title">Agency Experience</div>
            </div>
            <div class="area-rating">
              <div 
                v-for="n in 5" 
                :key="n"
                class="rating-star"
                :class="{ filled: backgroundData.agency >= n }"
                @click="rateBackground('agency', n)"
              >
                <v-icon size="medium">{{ backgroundData.agency >= n ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </div>
            </div>
          </div>
          
          <div class="background-area">
            <div class="area-header">
              <div class="area-icon">
                <v-icon size="large">mdi-school</v-icon>
              </div>
              <div class="area-title">Academia Experience</div>
            </div>
            <div class="area-rating">
              <div 
                v-for="n in 5" 
                :key="n"
                class="rating-star"
                :class="{ filled: backgroundData.academia >= n }"
                @click="rateBackground('academia', n)"
              >
                <v-icon size="medium">{{ backgroundData.academia >= n ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </div>
            </div>
          </div>
          
          <div class="background-area">
            <div class="area-header">
              <div class="area-icon">
                <v-icon size="large">mdi-bullhorn</v-icon>
              </div>
              <div class="area-title">Marketing Experience</div>
            </div>
            <div class="area-rating">
              <div 
                v-for="n in 5" 
                :key="n"
                class="rating-star"
                :class="{ filled: backgroundData.marketing >= n }"
                @click="rateBackground('marketing', n)"
              >
                <v-icon size="medium">{{ backgroundData.marketing >= n ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </div>
            </div>
          </div>
          
          <div class="background-area">
            <div class="area-header">
              <div class="area-icon">
                <v-icon size="large">mdi-chart-bar</v-icon>
              </div>
              <div class="area-title">Market Research Experience</div>
            </div>
            <div class="area-rating">
              <div 
                v-for="n in 5" 
                :key="n"
                class="rating-star"
                :class="{ filled: backgroundData.marketResearch >= n }"
                @click="rateBackground('marketResearch', n)"
              >
                <v-icon size="medium">{{ backgroundData.marketResearch >= n ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Additional Comments -->
      <div class="form-group">
        <label for="additionalInfo">Anything else you'd like to tell us about your experience? (Optional)</label>
        <textarea
          id="additionalInfo"
          v-model="additionalInfo"
          class="form-textarea"
          placeholder="Share any additional information about your expertise or experience that might be relevant"
          rows="5"
        ></textarea>
      </div>
      
      <!-- General error banner -->
      <div v-if="generalError" class="error-message general-error">{{ generalError }}</div>
      
      <!-- Summary Section -->
      <div class="summary-section">
        <h3 class="summary-title">
          <v-icon>mdi-check-circle</v-icon> Registration Almost Complete
        </h3>
        <p class="summary-text">
          Thank you for providing your professional information. By clicking "Complete Registration" below, 
          your expert profile will be submitted for review. We will notify you once your profile is 
          approved and you can begin participating in TCCC panel activities.
        </p>
      </div>
      
      <div class="action-buttons">
        <button 
          class="btn btn-back"
          @click="handleBack"
          :disabled="isSubmitting"
        >
          <v-icon>mdi-arrow-left</v-icon> Back
        </button>
        
        <button 
          class="btn btn-finish"
          @click="handleFinish"
          :disabled="isSubmitting"
        >
          <template v-if="isSubmitting">
            <v-progress-circular indeterminate color="white" size="20" class="spinner" />
          </template>
          <template v-else>
            <v-icon>mdi-check</v-icon> Complete Registration
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  name: 'RegisterStep8',
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      backgroundData: {
        agency: this.formData.background?.agency || 0,
        academia: this.formData.background?.academia || 0,
        marketing: this.formData.background?.marketing || 0,
        marketResearch: this.formData.background?.marketResearch || 0
      },
      additionalInfo: this.formData.additionalInfo || '',
      // Form options should be passed from previous steps
      formOptions: this.formData.formOptions || {},
      isSubmitting: false,
      error: '',
      generalError: ''
    };
  },
  methods: {
    rateBackground(area, rating) {
      // If already selected, clicking again sets to 0 (assuming 0 means not rated)
      if (this.backgroundData[area] === rating) {
         this.backgroundData[area] = 0;
      } else {
         this.backgroundData[area] = rating;
      }
      this.error = ''; // Clear error on interaction
    },
    extractValidationMessages(data) {
      const messages = [];
      if (!data || typeof data !== 'object') return messages;

      const errors = data.errors || data;
      if (typeof errors === 'string') {
        messages.push(errors);
        return messages;
      }
      if (typeof errors !== 'object') return messages;

      const fieldLabels = {
        first_name: 'First name',
        last_name: 'Last name',
        mobile_phone: 'Phone number',
        birthdate: 'Date of birth',
        gender: 'Gender',
        expertise_level: 'Expertise level',
        profile_picture: 'Profile picture',
        current_company: 'Company',
        agency_experience: 'Agency experience',
        academia_experience: 'Academia experience',
        marketing_experience: 'Marketing experience',
        market_research_experience: 'Market research experience',
        expertise_areas: 'Expertise areas',
        industries: 'Industries',
        geographical_expertise: 'Geographic expertise',
      };

      for (const [field, value] of Object.entries(errors)) {
        if (field === 'message' || field === 'detail') continue;
        const label = fieldLabels[field] || 'Field';
        if (Array.isArray(value)) {
          messages.push(`${label}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          messages.push(`${label}: ${value}`);
        } else if (typeof value === 'object' && value !== null) {
          const nested = Object.values(value)
            .flat()
            .filter(v => typeof v === 'string');
          if (nested.length > 0) {
            messages.push(`${label}: ${nested.join(', ')}`);
          }
        }
      }
      return messages;
    },
    validateForm() {
      this.error = '';
      this.generalError = '';
      return true;
    },
    handleBack() {
      // Update parent data before going back
       this.$emit('update:form-data', {
          ...this.formData,
          background: this.backgroundData,
          additionalInfo: this.additionalInfo
        });
      this.$emit('prev-step');
    },
    prepareProfilePayload() {
        // Aggregate data from formData (Steps 4-8)
        const profileData = {
            // Step 4
            first_name: this.formData.firstName,
            last_name: this.formData.lastName,
            mobile_phone: this.formData.mobilePhone,
            birthdate: this.formData.birthdate,
            gender: this.formData.gender,
            linkedin_profile_url: this.formData.linkedinProfileUrl,
            current_company: this.formData.currentCompany,

            // Step 5: Brand Experiences - REMOVED (No longer collected)
            // other_brand_experience: this.formData.otherBrandExperience || '',

            // Step 6 (previously Step 6)
            expertise_level: this.formData.expertiseLevel,
            // New fields for expertise areas and industries
            other_expertise: this.formData.otherExpertise || '',
            other_industries: this.formData.otherIndustries || '',

            // Step 7 (previously Step 7)
            age_expertise_children: this.formData.ageExpertise?.children || 0,
            age_expertise_teenagers: this.formData.ageExpertise?.teenagers || 0,
            age_expertise_adults: this.formData.ageExpertise?.adults || 0,
            age_expertise_seniors: this.formData.ageExpertise?.seniors || 0,

            // Step 8 (previously Step 8)
            agency_experience: this.backgroundData.agency || 0,
            design_experience: 0, // Keep for compatibility, but always 0
            academia_experience: this.backgroundData.academia || 0,
            marketing_experience: this.backgroundData.marketing || 0,
            market_research_experience: this.backgroundData.marketResearch || 0,
            additional_info: this.additionalInfo
        };

        // Handle M2M fields with the new structure
        if (this.formData.expertiseAreas && this.formData.expertiseAreas.length > 0) {
            profileData.expertise_areas = this.formData.expertiseAreas;
        }
        
        if (this.formData.industries && this.formData.industries.length > 0) {
            profileData.industries = this.formData.industries;
        }
        
        if (this.formData.geographicalExpertise && this.formData.geographicalExpertise.length > 0) {
            profileData.geographical_expertise = this.formData.geographicalExpertise;
        }
        
        // REMOVED - Brand experience no longer collected in registration
        // if (this.formData.brandExperience && this.formData.brandExperience.length > 0) {
        //     // Send simple list of brand IDs for backend handling
        //     profileData.brands = this.formData.brandExperience;
        // 
        //     // Keep the detailed brand_experiences object array in case backend expects it elsewhere
        //     profileData.brand_experiences = this.formData.brandExperience.map(brandId => ({
        //         brand_id: brandId,
        //         start_date: null,
        //         end_date: null,
        //         is_current: false
        //     }));
        // }

        // Clean up null/undefined values if necessary
        Object.keys(profileData).forEach(key => {
            if (profileData[key] === undefined || profileData[key] === null) {
                delete profileData[key];
            }
        });

        // Remove any outdated fields that no longer exist in the backend model
        delete profileData.curriculum_highlights;
        delete profileData.curriculumHighlights;
        
        return profileData;
    },
    async handleFinish() {
      if (this.validateForm()) {
        this.isSubmitting = true;
        this.error = '';
        this.generalError = '';

        const profilePayload = this.prepareProfilePayload();

        /*
         * If the user selected a profile picture in Step 4, it will be available as
         * `this.formData.profilePicture` (File instance).  In that case we need to
         * switch to multipart/form-data so the image is sent correctly.
         */
        let payloadToSend = profilePayload;
        if (this.formData.profilePicture instanceof File) {
          const fd = new FormData();
          // Append simple fields (numbers / strings).  For objects or arrays we JSON-stringify.
          for (const [key, value] of Object.entries(profilePayload)) {
            if (value === undefined || value === null) continue;

            // If the value is an array of primitives (numbers/strings), append each item individually
            if (Array.isArray(value) && value.every(v => (typeof v !== 'object'))) {
              value.forEach(item => fd.append(key, item));
              continue;
            }

            // For any objects or arrays of objects, stringify
            if (typeof value === 'object') {
              fd.append(key, JSON.stringify(value));
            } else {
              fd.append(key, value);
            }
          }
          // Finally append the image file
          fd.append('profile_picture', this.formData.profilePicture);
          payloadToSend = fd;
        }

        try {
          console.log('Submitting expert profile payload:', payloadToSend);
          // We call the lower-level helper to preserve full config control.
          const response = await apiService.put('/experts/profile/me/', payloadToSend);
          console.log('Profile Update Success:', response.data);
          
          // Mark registration as complete
          try {
            await apiService.post('/business/complete-registration/');
            console.log('Registration marked as complete');
          } catch (completeError) {
            console.warn('Failed to mark registration as complete:', completeError);
            // Don't fail the entire process if this fails
          }
          
          this.$emit('finish');
        } catch (error) {
          console.error('Expert profile update failed:', error);

          if (error.response && error.response.status >= 400 && error.response.status < 500 && error.response.data) {
            const validationMessages = this.extractValidationMessages(error.response.data);
            if (validationMessages.length > 0) {
              this.generalError = 'Please fix the following issues: ' + validationMessages.join('. ') + '.';
              this.isSubmitting = false;
              return;
            }
          }

          try {
            console.log('Falling back to direct axios call for expert profile update...');
            const axios = await import('axios');
            // Retrieve token from localStorage
            const token = localStorage.getItem('authToken');
            const headers = {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            };
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            const directResponse = await axios.default.post(
              `${process.env.VUE_APP_API_BASE_URL}/experts/profile/me/`,
              payloadToSend,
              {
                headers: headers,
                timeout: 10000
              }
            );
            console.log('Direct axios expert profile update success:', directResponse.data);
            
            // Mark registration as complete
            try {
              await axios.default.post(
                `${process.env.VUE_APP_API_BASE_URL}/business/complete-registration/`,
                {},
                {
                  headers: headers,
                  timeout: 10000
                }
              );
              console.log('Registration marked as complete');
            } catch (completeError) {
              console.warn('Failed to mark registration as complete:', completeError);
              // Don't fail the entire process if this fails
            }
            
            this.$emit('finish');
          } catch (directError) {
            console.error('Direct axios expert profile update failed:', directError);
            if (directError.response && directError.response.data) {
              const errorData = directError.response.data;
              const validationMessages = this.extractValidationMessages(errorData);
              if (validationMessages.length > 0) {
                this.generalError = 'Please fix the following issues: ' + validationMessages.join('. ') + '.';
              } else {
                this.generalError = 'Failed to save profile. Please review your information and try again.';
              }
            } else {
              this.generalError = 'Failed to save profile. Please check your connection and try again.';
            }
          }
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  }
};
</script>

<style scoped>
.background-step {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 900px;
  margin: 0 auto;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
}

.step-description {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
}

.background-content {
  padding: 0 10px;
}

.form-group {
  margin-bottom: 30px;
}

.background-label {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
  display: block;
}

/* Background Areas */
.background-areas {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.background-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.background-area:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.area-header {
  display: flex;
  align-items: center;
}

.area-icon {
  color: #e94057;
  margin-right: 15px;
}

.area-title {
  font-weight: 500;
  font-size: 16px;
}

.area-rating {
  display: flex;
  gap: 5px;
}

.rating-star {
  cursor: pointer;
  color: #ccc;
  font-size: 20px;
}

/* Force larger icon size for the star icon itself */
.rating-star .v-icon {
  font-size: 24px !important;
}

.rating-star.filled {
  color: #e94057;
}

/* Additional Info */
.form-textarea {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
}

/* Summary Section */
.summary-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 30px 0;
  border-left: 4px solid #4A6282;
}

.summary-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  color: #4A6282;
  margin-bottom: 10px;
}

.summary-title .v-icon {
  margin-right: 10px;
}

.summary-text {
  color: #555;
  line-height: 1.6;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn {
  padding: 15px 30px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-back {
  background-color: #f0f0f0;
  color: #333;
}

.btn-back:hover {
  background-color: #e0e0e0;
}

.btn-finish {
  background-color: #4A6282;
  color: white;
}

.btn-finish:hover {
  background-color: #3d5270;
}

.btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #e94057;
  font-size: 14px;
  margin-top: 5px;
}

.general-error {
  text-align: center;
  margin-top: 20px;
}

.spinner {
  margin: 0;
}

@media (max-width: 768px) {
  .background-area {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .area-rating {
    margin-top: 15px;
  }
}
</style> 